import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChangeEvent, FormEvent, ReactNode, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '@/lib/api';
import type { AdminWorkshop, AdminWorkshopOverview, ImportDetails } from '@/lib/types';

type UploadCardProps = {
  title: string;
  description: string;
  fileLabel: string;
  accept: string;
  buttonLabel: string;
  loadingLabel: string;
  onFileChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onSubmit: () => void;
  isPending: boolean;
  isReady: boolean;
  error?: unknown;
  extra?: ReactNode;
  details?: ImportDetails | null;
  onPublish?: (details: ImportDetails) => void;
  isPublishing?: boolean;
  publishError?: unknown;
  helperNotice?: ReactNode;
};

function UploadCard({
  title,
  description,
  fileLabel,
  accept,
  buttonLabel,
  loadingLabel,
  onFileChange,
  onSubmit,
  isPending,
  isReady,
  error,
  extra,
  details,
  onPublish,
  isPublishing = false,
  publishError,
  helperNotice,
}: UploadCardProps) {
  const previewColumns = Object.keys(details?.preview[0] ?? {});
  const hasBlockingIssues = (details?.issues ?? []).some((issue) => issue.severity === 'error');

  return (
    <section className="panel">
      <div className="panel__header">
        <span className="eyebrow">Upload</span>
        <h3>{title}</h3>
        <p>{description}</p>
      </div>

      <div className="stack">
        {extra}

        <label className="field">
          <span>{fileLabel}</span>
          <input type="file" accept={accept} onChange={onFileChange} />
        </label>

        {error instanceof Error ? <p className="notice notice--error">{error.message}</p> : null}
        {publishError instanceof Error ? <p className="notice notice--error">{publishError.message}</p> : null}
        {helperNotice}

        <button className="button" type="button" onClick={onSubmit} disabled={!isReady || isPending}>
          {isPending ? loadingLabel : buttonLabel}
        </button>

        {details ? (
          <div className="import-preview">
            <div className="import-preview__header">
              <div>
                <strong>{details.filename}</strong>
                <p className="notice notice--soft">
                  Status: {details.status === 'published' ? 'Veröffentlicht' : details.status === 'validated' ? 'Bereit' : 'Prüfen'}
                </p>
              </div>
              {onPublish ? (
                <button
                  className="button button--ghost"
                  type="button"
                  onClick={() => onPublish(details)}
                  disabled={isPublishing || details.status === 'published' || hasBlockingIssues}
                >
                  {details.status === 'published' ? 'Veröffentlicht' : isPublishing ? 'Veröffentlicht…' : 'Tabelle übernehmen'}
                </button>
              ) : null}
            </div>

            <div className="stat-grid">
              {Object.entries(details.summary).map(([key, value]) => (
                <div className="stat-card" key={key}>
                  <span>{humanizeKey(key)}</span>
                  <strong>{String(value)}</strong>
                </div>
              ))}
            </div>

            {details.issues.length ? (
              <div className="issue-list">
                {details.issues.map((issue) => (
                  <article className={`issue issue--${issue.severity}`} key={`${issue.rowNumber}-${issue.field}-${issue.message}`}>
                    <strong>
                      Zeile {issue.rowNumber} · {issue.field}
                    </strong>
                    <p>{issue.message}</p>
                  </article>
                ))}
              </div>
            ) : (
              <p className="notice notice--soft">Keine Blocker gefunden. Die Vorschau kann übernommen werden.</p>
            )}

            {previewColumns.length ? (
              <div className="preview-table-wrap">
                <table className="preview-table">
                  <thead>
                    <tr>
                      {previewColumns.map((column) => (
                        <th key={column}>{humanizeKey(column)}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {details.preview.map((row, index) => (
                      <tr key={index}>
                        {previewColumns.map((column) => (
                          <td key={column}>{formatPreviewValue(row[column])}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}

function humanizeKey(value: string): string {
  return value
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatPreviewValue(value: unknown): string {
  if (typeof value === 'boolean') {
    return value ? 'Ja' : 'Nein';
  }

  if (value === null || value === undefined || value === '') {
    return '—';
  }

  return String(value);
}

type WorkshopEditFormProps = {
  workshop: AdminWorkshop;
  options: AdminWorkshopOverview['options'];
  onCancel: () => void;
  onSubmit: (payload: Record<string, unknown>) => void;
  isPending: boolean;
  error?: unknown;
};

function WorkshopEditForm({ workshop, options, onCancel, onSubmit, isPending, error }: WorkshopEditFormProps) {
  const [title, setTitle] = useState(workshop.title);
  const [subtitle, setSubtitle] = useState(workshop.subtitle ?? '');
  const [description, setDescription] = useState(workshop.description ?? '');
  const [speaker, setSpeaker] = useState(workshop.speaker ?? '');
  const [speakerTag, setSpeakerTag] = useState(workshop.speakerTag ?? '');
  const [startTime, setStartTime] = useState(workshop.startTime);
  const [roomName, setRoomName] = useState(workshop.roomName);
  const [capacity, setCapacity] = useState(workshop.capacity);
  const [isBookable, setIsBookable] = useState(workshop.isBookable);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    onSubmit({
      title: title.trim(),
      subtitle: subtitle.trim() || null,
      description: description.trim() || null,
      speaker: speaker.trim() || null,
      speakerTag: speakerTag.trim() || null,
      formatLabel: workshop.formatLabel,
      trackLabel: workshop.trackLabel,
      startTime: startTime.trim(),
      roomName: roomName.trim(),
      capacity: Number.isFinite(capacity) ? capacity : 0,
      isBookable,
    });
  }

  const datalistId = `workshop-${workshop.id}-options`;
  return (
    <form className="workshop-edit-form" onSubmit={handleSubmit}>
      <datalist id={`${datalistId}-rooms`}>
        {options.rooms.map((room) => (
          <option value={room} key={room} />
        ))}
      </datalist>
      <datalist id={`${datalistId}-times`}>
        {options.slotTimes.map((time) => (
          <option value={time} key={time} />
        ))}
      </datalist>

      <div className="workshop-edit-form__grid">
        <label className="field">
          <span>Titel</span>
          <input value={title} onChange={(event) => setTitle(event.target.value)} required />
        </label>
        <label className="field">
          <span>Speaker</span>
          <input value={speaker} onChange={(event) => setSpeaker(event.target.value)} />
        </label>
        <label className="field">
          <span>Titel der Referentin</span>
          <input value={speakerTag} onChange={(event) => setSpeakerTag(event.target.value)} />
        </label>
        <label className="field">
          <span>Uhrzeit</span>
          <input
            list={`${datalistId}-times`}
            value={startTime}
            onChange={(event) => setStartTime(event.target.value)}
            placeholder="10:00"
            required
          />
        </label>
        <label className="field">
          <span>Raum</span>
          <input
            list={`${datalistId}-rooms`}
            value={roomName}
            onChange={(event) => setRoomName(event.target.value)}
            required
          />
        </label>
        <label className="field">
          <span>Kapazität</span>
          <input
            type="number"
            min={0}
            value={capacity}
            onChange={(event) => setCapacity(Number(event.target.value))}
          />
        </label>
        <label className="field field--checkbox">
          <input
            type="checkbox"
            checked={isBookable}
            onChange={(event) => setIsBookable(event.target.checked)}
          />
          <span>Buchbar</span>
        </label>
      </div>

      <label className="field">
        <span>Untertitel</span>
        <input value={subtitle} onChange={(event) => setSubtitle(event.target.value)} />
      </label>

      <label className="field">
        <span>Beschreibung (Plus-Popup)</span>
        <textarea
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          rows={3}
        />
      </label>

      {error instanceof Error ? <p className="notice notice--error">{error.message}</p> : null}

      <div className="inline-actions">
        <button className="button" type="submit" disabled={isPending}>
          {isPending ? 'Speichert…' : 'Änderungen speichern'}
        </button>
        <button className="button button--ghost" type="button" onClick={onCancel} disabled={isPending}>
          Abbrechen
        </button>
      </div>
    </form>
  );
}

function groupWorkshops(workshops: AdminWorkshop[]) {
  const groups = new Map<string, AdminWorkshop[]>();

  workshops.forEach((workshop) => {
    const key = `${workshop.startTime} – ${workshop.endTime}`;
    const existing = groups.get(key) ?? [];
    existing.push(workshop);
    groups.set(key, existing);
  });

  return Array.from(groups.entries()).map(([slotLabel, items]) => ({
    slotLabel,
    items,
  }));
}

export function AdminImportsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [eventDate, setEventDate] = useState('2026-05-09');
  const [programFile, setProgramFile] = useState<File | null>(null);
  const [attendeeFile, setAttendeeFile] = useState<File | null>(null);
  const [programImport, setProgramImport] = useState<ImportDetails | null>(null);
  const [attendeeImport, setAttendeeImport] = useState<ImportDetails | null>(null);

  const sessionQuery = useQuery({
    queryKey: ['admin-session'],
    queryFn: api.adminSession,
  });

  useEffect(() => {
    if (sessionQuery.error instanceof ApiError && sessionQuery.error.status === 401) {
      navigate('/admin/login');
    }
  }, [navigate, sessionQuery.error]);

  const workshopsQuery = useQuery({
    queryKey: ['admin-workshops'],
    queryFn: api.adminWorkshops,
    enabled: sessionQuery.isSuccess,
  });

  useEffect(() => {
    if (workshopsQuery.data?.eventDate) {
      setEventDate((current) => current || workshopsQuery.data?.eventDate || '2026-05-09');
    }
  }, [workshopsQuery.data?.eventDate]);

  const uploadProgramMutation = useMutation({
    mutationFn: (file: File) => api.importProgram(file, eventDate),
    onSuccess: (details) => setProgramImport(details),
  });

  const uploadAttendeesMutation = useMutation({
    mutationFn: (file: File) => api.importAttendees(file),
    onSuccess: (details) => setAttendeeImport(details),
  });

  const publishMutation = useMutation({
    mutationFn: (id: number) => api.publishImport(id),
    onSuccess: async (details) => {
      if (details.kind === 'program') {
        setProgramImport(details);
      } else {
        setAttendeeImport(details);
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['admin-workshops'] }),
        queryClient.invalidateQueries({ queryKey: ['agenda'] }),
        queryClient.invalidateQueries({ queryKey: ['my-bookings'] }),
        queryClient.invalidateQueries({ queryKey: ['me'] }),
      ]);
    },
  });

  const resetBookingsMutation = useMutation({
    mutationFn: () => api.resetBookings(workshopsQuery.data?.eventDate),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['admin-workshops'] }),
        queryClient.invalidateQueries({ queryKey: ['agenda'] }),
        queryClient.invalidateQueries({ queryKey: ['my-bookings'] }),
        queryClient.invalidateQueries({ queryKey: ['me'] }),
      ]);
    },
  });

  const [editingWorkshopId, setEditingWorkshopId] = useState<number | null>(null);

  const invalidateWorkshopState = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['admin-workshops'] }),
      queryClient.invalidateQueries({ queryKey: ['agenda'] }),
      queryClient.invalidateQueries({ queryKey: ['my-bookings'] }),
      queryClient.invalidateQueries({ queryKey: ['me'] }),
    ]);

  const updateWorkshopMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: Record<string, unknown> }) =>
      api.updateWorkshop(id, payload),
    onSuccess: async () => {
      setEditingWorkshopId(null);
      await invalidateWorkshopState();
    },
  });

  const resetWorkshopBookingsMutation = useMutation({
    mutationFn: (id: number) => api.resetWorkshopBookings(id),
    onSuccess: async () => {
      await invalidateWorkshopState();
    },
  });

  const logoutMutation = useMutation({
    mutationFn: api.adminLogout,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin-session'] });
      navigate('/admin/login');
    },
  });

  const workshops = workshopsQuery.data?.workshops ?? [];
  const summary = useMemo(() => {
    return {
      workshops: workshops.length,
      angemeldet: workshops.reduce((sum, workshop) => sum + workshop.bookedCount, 0),
      freiePlaetze: workshops.reduce((sum, workshop) => sum + (workshop.remainingSeats ?? 0), 0),
      teilnehmerinnen: workshopsQuery.data?.participants.length ?? 0,
    };
  }, [workshops, workshopsQuery.data?.participants.length]);

  const groupedWorkshops = useMemo(() => groupWorkshops(workshops), [workshops]);
  return (
    <div className="stack stack--xl">
      <section className="panel panel--accent">
        <div className="panel__header">
          <span className="eyebrow">Admin Studio</span>
          <h2>{workshopsQuery.data?.eventLabel ?? 'Event-Dashboard'}</h2>
          <p>Programm und Teilnehmerinnen werden nur noch per Tabellen gepflegt. Upload, prüfen, übernehmen, fertig.</p>
        </div>

        <div className="stat-grid">
          {Object.entries(summary).map(([key, value]) => (
            <div className="stat-card" key={key}>
              <span>{humanizeKey(key)}</span>
              <strong>{value}</strong>
            </div>
          ))}
        </div>

        <div className="inline-actions">
          <button className="button button--ghost" type="button" onClick={() => logoutMutation.mutate()} disabled={logoutMutation.isPending}>
            {logoutMutation.isPending ? 'Logout…' : 'Admin abmelden'}
          </button>
        </div>
      </section>

      <div className="admin-grid">
        <UploadCard
          title="Programm-Tabelle"
          description="CSV oder Excel hochladen, Vorschau prüfen und das Programm aktualisieren. Bestehende Kurse werden nach Uhrzeit und Raum aktualisiert, neue Kurse werden angelegt. Buchungen bleiben dabei erhalten."
          fileLabel="Programmdatei"
          accept=".csv,.xlsx,.xls"
          buttonLabel="Programm prüfen"
          loadingLabel="Datei wird geprüft…"
          onFileChange={(event) => setProgramFile(event.target.files?.[0] ?? null)}
          onSubmit={() => {
            if (programFile) {
              uploadProgramMutation.mutate(programFile);
            }
          }}
          isPending={uploadProgramMutation.isPending}
          isReady={programFile !== null}
          error={uploadProgramMutation.error}
          extra={(
            <label className="field">
              <span>Event-Datum</span>
              <input type="date" value={eventDate} onChange={(event) => setEventDate(event.target.value)} />
            </label>
          )}
          details={programImport}
          onPublish={(details) => publishMutation.mutate(details.id)}
          isPublishing={publishMutation.isPending && publishMutation.variables === programImport?.id}
          publishError={publishMutation.variables === programImport?.id ? publishMutation.error : null}
          helperNotice={
            <p className="notice notice--soft">
              Nicht buchbare Slots steuerst du per Spalte <strong>Buchbar</strong>, <strong>Nicht buchbar</strong> oder <strong>is bookable</strong> mit
              Werten wie ja/nein, x oder 1/0. Die Reihenfolge innerhalb derselben Uhrzeit steuerst du optional per Spalte <strong>Reihenfolge</strong>,
              {' '}<strong>Sortierung</strong> oder <strong>Order</strong>. Ohne Spalte gilt die Tabellenreihenfolge.
            </p>
          }
        />

        <UploadCard
          title="Teilnehmerinnen-Tabelle"
          description="Auch Logins kommen aus der Tabelle. Unterstützt werden klassische Felder wie Nachname/Code genauso wie Username/Password."
          fileLabel="Teilnehmerinnen-Datei"
          accept=".csv,.xlsx,.xls"
          buttonLabel="Teilnehmerinnen prüfen"
          loadingLabel="Datei wird geprüft…"
          onFileChange={(event) => setAttendeeFile(event.target.files?.[0] ?? null)}
          onSubmit={() => {
            if (attendeeFile) {
              uploadAttendeesMutation.mutate(attendeeFile);
            }
          }}
          isPending={uploadAttendeesMutation.isPending}
          isReady={attendeeFile !== null}
          error={uploadAttendeesMutation.error}
          details={attendeeImport}
          onPublish={(details) => publishMutation.mutate(details.id)}
          isPublishing={publishMutation.isPending && publishMutation.variables === attendeeImport?.id}
          publishError={publishMutation.variables === attendeeImport?.id ? publishMutation.error : null}
        />
      </div>

      <section className="panel">
        <div className="panel__header">
          <span className="eyebrow">Live-Buchungsstand</span>
          <h3>Teilnehmerinnen je Workshop</h3>
          <p>Freie Plätze und Anmeldungen sind direkt sichtbar. Jeder Workshop kann einzeln geöffnet und gedruckt werden.</p>
        </div>

        <div className="inline-actions">
          <button
            className="button button--ghost"
            type="button"
            onClick={() => window.open('/admin/overview/print', '_blank', 'noopener')}
          >
            Komplettübersicht drucken
          </button>
          <button
            className="button button--ghost button--danger"
            type="button"
            disabled={resetBookingsMutation.isPending || summary.angemeldet === 0}
            onClick={() => {
              if (summary.angemeldet === 0) {
                return;
              }

              const shouldReset = window.confirm(
                `Alle ${summary.angemeldet} aktuellen Anmeldungen für ${workshopsQuery.data?.eventLabel ?? 'das Event'} wirklich löschen?`,
              );

              if (shouldReset) {
                resetBookingsMutation.mutate();
              }
            }}
          >
            {resetBookingsMutation.isPending ? 'Setzt zurück…' : 'Alle Anmeldungen resetten'}
          </button>
        </div>

        {resetBookingsMutation.error instanceof Error ? (
          <p className="notice notice--error">{resetBookingsMutation.error.message}</p>
        ) : null}

        {resetBookingsMutation.data ? (
          <p className="notice notice--soft">
            {resetBookingsMutation.data.deletedCount} Anmeldungen wurden zurückgesetzt.
          </p>
        ) : null}

        <div className="stack">
          {groupedWorkshops.map((group) => (
            <section className="slot-admin-group" key={group.slotLabel}>
              <div className="slot-admin-group__header">
                <strong>{group.slotLabel}</strong>
                <span>{group.items.length} Workshops</span>
              </div>

              <div className="workshop-admin-list">
                {group.items.map((workshop) => {
                  const isEditing = editingWorkshopId === workshop.id;
                  const isResetting =
                    resetWorkshopBookingsMutation.isPending &&
                    resetWorkshopBookingsMutation.variables === workshop.id;
                  const resetError =
                    resetWorkshopBookingsMutation.variables === workshop.id
                      ? resetWorkshopBookingsMutation.error
                      : null;
                  const resetResult =
                    resetWorkshopBookingsMutation.variables === workshop.id
                      ? resetWorkshopBookingsMutation.data
                      : null;
                  const updateError =
                    updateWorkshopMutation.variables?.id === workshop.id
                      ? updateWorkshopMutation.error
                      : null;
                  const isUpdating =
                    updateWorkshopMutation.isPending && updateWorkshopMutation.variables?.id === workshop.id;

                  return (
                    <details className="workshop-admin-card" key={workshop.id} open={workshop.bookedCount > 0 || isEditing}>
                      <summary className="workshop-admin-card__summary">
                        <div>
                          <strong>
                            <span className="room-label">{workshop.roomName}</span> · {workshop.title}
                          </strong>
                          <p>
                            {workshop.trackLabel ?? 'Ohne Track'} · {workshop.startTime} – {workshop.endTime} ·{' '}
                            {workshop.bookedCount} angemeldet ·{' '}
                            {workshop.isBookable ? `${workshop.remainingSeats ?? 0} frei` : 'nicht buchbar'}
                          </p>
                        </div>
                        <span className="workshop-admin-card__toggle">Details</span>
                      </summary>

                      <div className="workshop-admin-card__body">
                        {workshop.subtitle ? <p className="session-card__subtitle">{workshop.subtitle}</p> : null}
                        {workshop.description ? <p className="notice notice--soft">{workshop.description}</p> : null}

                        <div className="inline-actions">
                          <button
                            className="button button--ghost"
                            type="button"
                            onClick={() => setEditingWorkshopId(isEditing ? null : workshop.id)}
                          >
                            {isEditing ? 'Bearbeiten schließen' : 'Bearbeiten'}
                          </button>
                          <button
                            className="button button--ghost button--danger"
                            type="button"
                            disabled={isResetting || workshop.bookedCount === 0}
                            onClick={() => {
                              const confirmed = window.confirm(
                                `Alle ${workshop.bookedCount} Anmeldungen für "${workshop.title}" (${workshop.roomName}, ${workshop.startTime}) wirklich zurücksetzen? Andere Workshops bleiben unverändert.`,
                              );

                              if (confirmed) {
                                resetWorkshopBookingsMutation.mutate(workshop.id);
                              }
                            }}
                          >
                            {isResetting ? 'Setzt zurück…' : 'Anmeldungen zurücksetzen'}
                          </button>
                          <button
                            className="button button--ghost"
                            type="button"
                            onClick={() => window.open(`/admin/workshops/${workshop.id}/print`, '_blank', 'noopener')}
                          >
                            Teilnehmerliste drucken
                          </button>
                        </div>

                        {resetError instanceof Error ? (
                          <p className="notice notice--error">{resetError.message}</p>
                        ) : null}

                        {resetResult ? (
                          <p className="notice notice--soft">
                            {resetResult.deletedCount} Anmeldungen für diesen Workshop wurden zurückgesetzt.
                          </p>
                        ) : null}

                        {isEditing && workshopsQuery.data ? (
                          <WorkshopEditForm
                            workshop={workshop}
                            options={workshopsQuery.data.options}
                            onCancel={() => setEditingWorkshopId(null)}
                            onSubmit={(payload) => updateWorkshopMutation.mutate({ id: workshop.id, payload })}
                            isPending={isUpdating}
                            error={updateError}
                          />
                        ) : null}

                        {workshop.participants.length ? (
                          <div className="participant-list participant-list--detailed">
                            {workshop.participants.map((participant) => (
                              <article className="participant-row" key={participant.id}>
                                <strong>{participant.displayName}</strong>
                                <code>{participant.code}</code>
                              </article>
                            ))}
                          </div>
                        ) : (
                          <p className="notice notice--soft">Noch keine Teilnehmerinnen angemeldet.</p>
                        )}
                      </div>
                    </details>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      </section>
    </div>
  );
}
